import { Link, useNavigate } from "@tanstack/react-router"
import { useState } from "react"

import type { GuideSet, GuideSummary } from "@repo/support/types"
import {
  RecordNav,
  type RecordNavGroup,
} from "@repo/ui/components/layouts/platform/record-nav"
import {
  Breadcrumbs,
  type BreadcrumbsItem,
} from "@repo/ui/components/ui/breadcrumb"
import { Button } from "@repo/ui/components/ui/button"
import { MenuIcon } from "@repo/ui/components/ui/icons"
import { If } from "@repo/ui/components/ui/if"
import { OptionPicker } from "@repo/ui/components/ui/option-picker"
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@repo/ui/components/ui/sheet"

export type GuidesShellProps = {
  sets: GuideSet[]
  set: GuideSet
  guides: GuideSummary[]
  /** The open guide's slug; null on the set's overview. */
  activeSlug: string | null
  breadcrumbs: BreadcrumbsItem[]
  children: React.ReactNode
}

/** The guides site's frame: the set's guides by category beside the page. */
export function GuidesShell({
  sets,
  set,
  guides,
  activeSlug,
  breadcrumbs,
  children,
}: GuidesShellProps) {
  const navigate = useNavigate()
  const [navOpen, setNavOpen] = useState(false)

  const groups: RecordNavGroup[] = [
    ...new Set(guides.map((guide) => guide.category)),
  ].map((category) => ({
    label: category,
    items: guides
      .filter((guide) => guide.category === category)
      .map((guide) => ({
        value: guide.slug,
        label: guide.title,
        render: (
          <Link
            to="/guides/$set/$slug"
            params={{ set: set.key, slug: guide.slug }}
          />
        ),
      })),
  }))

  const nav = (
    <div className="flex flex-col gap-5">
      <If condition={sets.length > 1}>
        <OptionPicker
          aria-label="Guide set"
          forceMode="select"
          size="sm"
          options={sets.map((item) => ({ value: item.key, label: item.label }))}
          value={set.key}
          onValueChange={(value) => {
            if (!value) return
            setNavOpen(false)
            void navigate({ to: "/guides/$set", params: { set: value } })
          }}
        />
      </If>
      <RecordNav
        groups={groups}
        value={activeSlug ?? ""}
        onSelect={() => setNavOpen(false)}
      />
    </div>
  )

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-5 px-4 py-6 md:py-8">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="icon-sm"
          className="md:hidden"
          aria-label="Browse guides"
          onClick={() => setNavOpen(true)}
        >
          <MenuIcon />
        </Button>
        <Breadcrumbs items={breadcrumbs} />
      </div>
      <div className="flex gap-8">
        <aside className="sticky top-20 hidden w-60 shrink-0 self-start md:block">
          {nav}
        </aside>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
      <Sheet open={navOpen} onOpenChange={setNavOpen} side="left">
        <SheetContent className="sm:max-w-72">
          <SheetHeader>
            <SheetTitle>{set.label}</SheetTitle>
          </SheetHeader>
          <SheetBody>{nav}</SheetBody>
        </SheetContent>
      </Sheet>
    </div>
  )
}

/** The trail from the guides home to the current place. */
export function guidesTrail(
  set: GuideSet | null,
  rest: BreadcrumbsItem[] = [],
): BreadcrumbsItem[] {
  return [
    { label: "Guides", render: <Link to="/guides" /> },
    ...(set
      ? [
          {
            label: set.label,
            ...(rest.length > 0
              ? {
                  render: <Link to="/guides/$set" params={{ set: set.key }} />,
                }
              : {}),
          },
        ]
      : []),
    ...rest,
  ]
}
